import { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";

const PAYMENT_METHODS = ["Credit Card", "Cash", "Check", "ACH", "Zelle"];
const FREQ_UNITS = ["Days", "Miles"];
const LINE_ITEM_TYPES = ["Labor Fee", "Other Fee", "Tax", "Discount"];
const BILL_TO_OPTIONS = ["Company", "Driver", "Other"];
const ORDER_STATUSES = ["pending", "in_progress", "completed", "cancelled"];
const LOGO_GREEN = "#238b58";
const LOGO_GREEN_LIGHT = "#e6f4ed";

const inputStyle = {
  width: "100%",
  border: "0.5px solid #e0e0e0",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  color: "#111",
  fontFamily: "inherit",
  background: "#fff",
};
const selectStyle = { ...inputStyle, cursor: "pointer" };
const labelStyle = {
  fontSize: "11px",
  fontWeight: "500",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
  display: "block",
};
const sectionHeaderStyle = {
  fontSize: "12px",
  fontWeight: "500",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "12px",
  paddingBottom: "8px",
  borderBottom: "0.5px solid #e8e8e8",
};

const DEFAULT_SCHEDULE = {
  service_type_ids: [],
  service_type_names: [],
  description: "",
  payment_method: "",
  equipment_type: "truck",
  truck_ids: [],
  frequency_unit: "Days",
  frequency_value: "",
  lead_days: "",
  status: "active",
};

const DEFAULT_ORDER = {
  invoice_number: "",
  truck_id: "",
  odometer: "",
  engine_hours: "",
  repair_type: "date_range",
  start_date: "",
  due_date: "",
  vendor: "",
  payment_method: "",
  bill_to: "Company",
  bill_to_driver_id: "",
  bill_to_other: "",
  charge_amount: "",
  notes: "",
  status: "pending",
};

const DEFAULT_LINE_ITEM = {
  service_type: "Labor Fee",
  description: "",
  quantity: 1,
  unit_rate: "",
  amount: 0,
};

function getStatusStyle(status) {
  const map = {
    pending: { background: "#faeeda", color: "#854f0b" },
    in_progress: { background: "#e6f1fb", color: "#185fa5" },
    completed: { background: "#eaf3de", color: "#3b6d11" },
    cancelled: { background: "#f0f0f0", color: "#6b7280" },
    active: { background: "#eaf3de", color: "#3b6d11" },
  };
  return map[status] || map.pending;
}

function fmt(n) {
  return `$${parseFloat(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Toggle group with logo green ──
function ToggleGroup({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          style={{
            padding: "6px 14px",
            borderRadius: "8px",
            border: "0.5px solid",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "all 0.15s",
            background: value === o ? LOGO_GREEN : "#fff",
            color: value === o ? "#fff" : "#555",
            borderColor: value === o ? LOGO_GREEN : "#e0e0e0",
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// ── Field wrapper ──
function Field({ label, children, required }) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: "#a32d2d" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ── Rich text editor ──
function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value)
      ref.current.innerHTML = value || "";
  }, [value]);
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };
  const btnS = {
    background: "#f5f5f5",
    border: "0.5px solid #e0e0e0",
    borderRadius: "4px",
    padding: "3px 7px",
    fontSize: "11px",
    fontWeight: "500",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#333",
  };
  return (
    <div
      style={{
        border: "0.5px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "4px",
          padding: "6px 8px",
          background: "#fafafa",
          borderBottom: "0.5px solid #e0e0e0",
          flexWrap: "wrap",
        }}
      >
        <button type="button" onClick={() => exec("bold")} style={btnS}>
          <b>B</b>
        </button>
        <button type="button" onClick={() => exec("italic")} style={btnS}>
          <i>I</i>
        </button>
        <button type="button" onClick={() => exec("underline")} style={btnS}>
          <u>U</u>
        </button>
        <button
          type="button"
          onClick={() => exec("strikeThrough")}
          style={btnS}
        >
          <s>S</s>
        </button>
        <div
          style={{ width: "0.5px", background: "#e0e0e0", margin: "0 3px" }}
        />
        <button type="button" onClick={() => exec("justifyLeft")} style={btnS}>
          ≡L
        </button>
        <button
          type="button"
          onClick={() => exec("justifyCenter")}
          style={btnS}
        >
          ≡C
        </button>
        <button type="button" onClick={() => exec("justifyRight")} style={btnS}>
          ≡R
        </button>
        <div
          style={{ width: "0.5px", background: "#e0e0e0", margin: "0 3px" }}
        />
        <button
          type="button"
          onClick={() => exec("insertUnorderedList")}
          style={btnS}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => exec("insertOrderedList")}
          style={btnS}
        >
          1. List
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || "")}
        style={{
          padding: "10px 12px",
          minHeight: "80px",
          fontSize: "13px",
          color: "#111",
          outline: "none",
          lineHeight: "1.6",
        }}
      />
    </div>
  );
}

// ── Service type multi-select (same chip pattern as driver multi-select) ──
function ServiceTypeMultiSelect({
  serviceTypes,
  selectedIds,
  selectedNames,
  onChange,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = serviceTypes
    .filter(
      (s) =>
        !selectedIds.includes(s.id) &&
        s.name.toLowerCase().includes(query.toLowerCase())
    )
    .slice(0, 10);
  const remove = (id) => {
    const idx = selectedIds.indexOf(id);
    onChange(
      selectedIds.filter((_, i) => i !== idx),
      selectedNames.filter((_, i) => i !== idx)
    );
  };
  const add = (s) => {
    onChange([...selectedIds, s.id], [...selectedNames, s.name]);
    setQuery("");
    setOpen(false);
  };
  return (
    <div ref={ref}>
      {selectedNames.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          {selectedNames.map((name, i) => (
            <div
              key={selectedIds[i]}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: LOGO_GREEN_LIGHT,
                color: LOGO_GREEN,
                borderRadius: "20px",
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {name}
              <button
                onMouseDown={() => remove(selectedIds[i])}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: LOGO_GREEN,
                  fontSize: "14px",
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input
          style={inputStyle}
          placeholder="Search and add service types..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && query.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "0.5px solid #e0e0e0",
              borderRadius: "8px",
              zIndex: 200,
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              marginTop: "4px",
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                No service types found
              </div>
            ) : (
              filtered.map((s) => (
                <div
                  key={s.id}
                  onMouseDown={() => add(s)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#111",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fff")
                  }
                >
                  {s.name}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Truck multi-select ──
function TruckMultiSelect({ trucks, selected, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selectedTrucks = trucks.filter((t) => selected.includes(t.id));
  const filtered = trucks
    .filter(
      (t) =>
        !selected.includes(t.id) &&
        `${t.unit_number} ${t.make} ${t.license_plate}`
          .toLowerCase()
          .includes(query.toLowerCase())
    )
    .slice(0, 8);
  return (
    <div ref={ref}>
      {selectedTrucks.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          {selectedTrucks.map((t) => (
            <div
              key={t.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: LOGO_GREEN_LIGHT,
                color: LOGO_GREEN,
                borderRadius: "20px",
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              Unit #{t.unit_number} — {t.make}
              <button
                onMouseDown={() =>
                  onChange(selected.filter((id) => id !== t.id))
                }
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: LOGO_GREEN,
                  fontSize: "14px",
                  lineHeight: 1,
                  padding: "0 2px",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input
          style={inputStyle}
          placeholder="Search and add trucks..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && query.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "0.5px solid #e0e0e0",
              borderRadius: "8px",
              zIndex: 200,
              maxHeight: "180px",
              overflowY: "auto",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              marginTop: "4px",
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                No trucks found
              </div>
            ) : (
              filtered.map((t) => (
                <div
                  key={t.id}
                  onMouseDown={() => {
                    onChange([...selected, t.id]);
                    setQuery("");
                    setOpen(false);
                  }}
                  style={{
                    padding: "9px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#111",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#fff")
                  }
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: LOGO_GREEN_LIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: "600",
                      color: LOGO_GREEN,
                      flexShrink: 0,
                    }}
                  >
                    #{t.unit_number}
                  </div>
                  <div>
                    <div style={{ fontWeight: "500" }}>
                      {t.make} {t.year}
                    </div>
                    <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {t.license_plate} · {t.state}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Truck single lookup ──
function TruckLookup({ trucks, value, onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = trucks.find((t) => t.id === value);
  useEffect(() => {
    setQuery(
      selected ? `Unit #${selected.unit_number} — ${selected.make}` : ""
    );
  }, [value, selected]);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = trucks
    .filter((t) =>
      `${t.unit_number} ${t.make} ${t.license_plate}`
        .toLowerCase()
        .includes(query.toLowerCase())
    )
    .slice(0, 8);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        placeholder="Search active trucks..."
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value) onSelect("");
        }}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        style={inputStyle}
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "0.5px solid #e0e0e0",
            borderRadius: "8px",
            zIndex: 200,
            maxHeight: "180px",
            overflowY: "auto",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            marginTop: "4px",
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "10px 12px",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              No trucks found
            </div>
          ) : (
            filtered.map((t) => (
              <div
                key={t.id}
                onMouseDown={() => {
                  onSelect(t.id);
                  setQuery(`Unit #${t.unit_number} — ${t.make}`);
                  setOpen(false);
                }}
                style={{
                  padding: "9px 12px",
                  cursor: "pointer",
                  fontSize: "13px",
                  color: "#111",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: value === t.id ? "#f0faf4" : "#fff",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    value === t.id ? "#f0faf4" : "#fff")
                }
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: LOGO_GREEN_LIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: "600",
                    color: LOGO_GREEN,
                    flexShrink: 0,
                  }}
                >
                  #{t.unit_number}
                </div>
                <div>
                  <div style={{ fontWeight: "500" }}>
                    {t.make} {t.year}
                  </div>
                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {t.license_plate} · {t.state}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Schedule Form ──
function ScheduleForm({
  form,
  setF,
  trucks,
  serviceTypes,
  lastServices,
  onSave,
  onClose,
  saving,
}) {
  const selectedTrucks = trucks.filter((t) => form.truck_ids.includes(t.id));
  return (
    <div style={{ display: "flex", gap: "16px" }}>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Payment method</div>
          <ToggleGroup
            options={PAYMENT_METHODS}
            value={form.payment_method}
            onChange={(v) => setF("payment_method", v)}
          />
        </div>

        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Service configuration</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <Field label="Service type(s)" required>
              <ServiceTypeMultiSelect
                serviceTypes={serviceTypes}
                selectedIds={form.service_type_ids}
                selectedNames={form.service_type_names}
                onChange={(ids, names) => {
                  setF("service_type_ids", ids);
                  setF("service_type_names", names);
                }}
              />
            </Field>
            <Field label="Description (optional)">
              <RichTextEditor
                value={form.description}
                onChange={(v) => setF("description", v)}
              />
            </Field>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Equipment selection</div>
          <Field label="Select trucks">
            <TruckMultiSelect
              trucks={trucks}
              selected={form.truck_ids}
              onChange={(v) => setF("truck_ids", v)}
            />
          </Field>
        </div>

        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Maintenance frequency</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <Field label="Frequency unit">
              <ToggleGroup
                options={FREQ_UNITS}
                value={form.frequency_unit}
                onChange={(v) => setF("frequency_unit", v)}
              />
            </Field>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <Field label={`Every X ${form.frequency_unit}`}>
                <input
                  type="number"
                  style={inputStyle}
                  value={form.frequency_value}
                  onChange={(e) => setF("frequency_value", e.target.value)}
                  placeholder="e.g. 90"
                  min="1"
                />
              </Field>
              <Field label="Create order before X days">
                <input
                  type="number"
                  style={inputStyle}
                  value={form.lead_days}
                  onChange={(e) => setF("lead_days", e.target.value)}
                  placeholder="e.g. 7"
                  min="1"
                />
              </Field>
            </div>
          </div>
        </div>

        <div
          style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              background: "#f0f0f0",
              color: "#555",
              fontSize: "13px",
              fontWeight: "500",
              padding: "8px 18px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              background: saving ? "#9ca3af" : LOGO_GREEN,
              color: "#fff",
              fontSize: "13px",
              fontWeight: "500",
              padding: "8px 18px",
              borderRadius: "8px",
              border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {saving ? "Saving..." : "Save schedule"}
          </button>
        </div>
      </div>

      <div style={{ width: "260px", flexShrink: 0 }}>
        <div
          style={{
            background: "#fafafa",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
            position: "sticky",
            top: "0",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#111",
              marginBottom: "12px",
            }}
          >
            Last service
          </div>
          {selectedTrucks.length === 0 ? (
            <div
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                textAlign: "center",
                padding: "20px 0",
              }}
            >
              No equipment selected yet
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {selectedTrucks.map((t) => {
                const last = lastServices.find(
                  (ls) =>
                    ls.truck_id === t.id &&
                    form.service_type_names.includes(ls.service_type_name)
                );
                return (
                  <div
                    key={t.id}
                    style={{
                      background: "#fff",
                      border: "0.5px solid #e8e8e8",
                      borderRadius: "8px",
                      padding: "10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#111",
                        marginBottom: "2px",
                      }}
                    >
                      Unit #{t.unit_number} — {t.make}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginBottom: "4px",
                      }}
                    >
                      {form.service_type_names.length > 0
                        ? form.service_type_names.join(", ")
                        : "No service selected"}
                    </div>
                    {last ? (
                      <>
                        <div style={{ fontSize: "10px", color: "#9ca3af" }}>
                          Last:{" "}
                          <span style={{ color: "#111", fontWeight: "500" }}>
                            {last.last_service_date}
                          </span>
                        </div>
                        {last.last_service_mileage && (
                          <div style={{ fontSize: "10px", color: "#9ca3af" }}>
                            Mileage:{" "}
                            <span style={{ color: "#111", fontWeight: "500" }}>
                              {last.last_service_mileage}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: "10px", color: "#9ca3af" }}>
                        No service history found
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Order Form ──
function OrderForm({
  form,
  setF,
  lineItems,
  setLineItems,
  trucks,
  drivers,
  onSave,
  onClose,
  saving,
  attachments,
  setAttachments,
  savedAttachments,
}) {
  const totalCost = lineItems.reduce(
    (s, i) => s + parseFloat(i.amount || 0),
    0
  );
  const outstanding = totalCost - parseFloat(form.charge_amount || 0);

  const updateLineItem = (idx, key, val) => {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const updated = { ...item, [key]: val };
        if (key === "quantity" || key === "unit_rate") {
          updated.amount =
            parseFloat(updated.quantity || 0) *
            parseFloat(updated.unit_rate || 0);
        }
        return updated;
      })
    );
  };

  const handleFiles = (files) => {
    const previews = Array.from(files).map((f) => ({
      file: f,
      name: f.name,
      type: f.type,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
    }));
    setAttachments((p) => [...p, ...previews]);
  };

  const repairToggleStyle = (v) => ({
    padding: "5px 12px",
    borderRadius: "6px",
    border: "0.5px solid",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    fontFamily: "inherit",
    background: form.repair_type === v ? LOGO_GREEN : "#fff",
    color: form.repair_type === v ? "#fff" : "#555",
    borderColor: form.repair_type === v ? LOGO_GREEN : "#e0e0e0",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <div
        style={{
          background: "#fff",
          border: "0.5px solid #e8e8e8",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={sectionHeaderStyle}>Invoice header</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="Invoice #">
            <input
              style={inputStyle}
              value={form.invoice_number}
              onChange={(e) => setF("invoice_number", e.target.value)}
              placeholder="e.g. INV-0042"
            />
          </Field>
          <Field label="Truck" required>
            <TruckLookup
              trucks={trucks}
              value={form.truck_id}
              onSelect={(v) => setF("truck_id", v)}
            />
          </Field>
          <Field label="Odometer">
            <input
              style={inputStyle}
              value={form.odometer}
              onChange={(e) => setF("odometer", e.target.value)}
              placeholder="miles"
            />
          </Field>
          <Field label="Engine hours">
            <input
              style={inputStyle}
              value={form.engine_hours}
              onChange={(e) => setF("engine_hours", e.target.value)}
              placeholder="hrs"
            />
          </Field>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "0.5px solid #e8e8e8",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={sectionHeaderStyle}>Scheduling</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <div>
            <label style={labelStyle}>Repair on/by</label>
            <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
              <button
                onClick={() => setF("repair_type", "date_range")}
                style={repairToggleStyle("date_range")}
              >
                Date range
              </button>
              <button
                onClick={() => setF("repair_type", "asap")}
                style={repairToggleStyle("asap")}
              >
                ASAP
              </button>
            </div>
            {form.repair_type === "date_range" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                <div>
                  <label style={labelStyle}>Start date</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={form.start_date}
                    onChange={(e) => setF("start_date", e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Due date</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={form.due_date}
                    onChange={(e) => setF("due_date", e.target.value)}
                  />
                </div>
              </div>
            )}
            {form.repair_type === "asap" && (
              <div
                style={{
                  background: "#faeeda",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  color: "#854f0b",
                }}
              >
                Order will be marked as ASAP priority
              </div>
            )}
          </div>
          <Field label="Preferred vendor">
            <input
              style={inputStyle}
              value={form.vendor}
              onChange={(e) => setF("vendor", e.target.value)}
              placeholder="Vendor name or shop"
            />
          </Field>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "0.5px solid #e8e8e8",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={sectionHeaderStyle}>Services & pricing</div>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
            }}
          >
            <thead>
              <tr style={{ background: "#fafafa" }}>
                {[
                  "Service type",
                  "Description",
                  "Qty",
                  "Unit rate",
                  "Amount",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 10px",
                      textAlign: "left",
                      fontSize: "10px",
                      fontWeight: "500",
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "0.5px solid #e8e8e8",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "0.5px solid #f0f0f0" }}>
                  <td style={{ padding: "6px 8px" }}>
                    <select
                      value={item.service_type}
                      onChange={(e) =>
                        updateLineItem(idx, "service_type", e.target.value)
                      }
                      style={{
                        border: "0.5px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "5px 8px",
                        fontSize: "12px",
                        background: "#fff",
                        color: "#111",
                        fontFamily: "inherit",
                        outline: "none",
                      }}
                    >
                      {LINE_ITEM_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <input
                      value={item.description}
                      onChange={(e) =>
                        updateLineItem(idx, "description", e.target.value)
                      }
                      placeholder="Description..."
                      style={{
                        border: "0.5px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "5px 8px",
                        fontSize: "12px",
                        width: "100%",
                        color: "#111",
                        fontFamily: "inherit",
                        outline: "none",
                        minWidth: "140px",
                      }}
                    />
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateLineItem(idx, "quantity", e.target.value)
                      }
                      min="1"
                      style={{
                        border: "0.5px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "5px 8px",
                        fontSize: "12px",
                        width: "55px",
                        color: "#111",
                        fontFamily: "inherit",
                        outline: "none",
                      }}
                    />
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <input
                      type="number"
                      value={item.unit_rate}
                      onChange={(e) =>
                        updateLineItem(idx, "unit_rate", e.target.value)
                      }
                      placeholder="0.00"
                      step="0.01"
                      style={{
                        border: "0.5px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "5px 8px",
                        fontSize: "12px",
                        width: "80px",
                        color: "#111",
                        fontFamily: "inherit",
                        outline: "none",
                      }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      fontWeight: "500",
                      color: "#3b6d11",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmt(item.amount)}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <button
                      onClick={() =>
                        setLineItems((p) => p.filter((_, i) => i !== idx))
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#9ca3af",
                        fontSize: "18px",
                        lineHeight: 1,
                        padding: "0 4px",
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <button
            onClick={() =>
              setLineItems((p) => [...p, { ...DEFAULT_LINE_ITEM }])
            }
            style={{
              background: "none",
              border: "0.5px solid #e0e0e0",
              borderRadius: "6px",
              padding: "5px 12px",
              fontSize: "12px",
              cursor: "pointer",
              color: "#6b7280",
              fontFamily: "inherit",
            }}
          >
            + Add line
          </button>
          <div style={{ fontSize: "13px", fontWeight: "500", color: "#111" }}>
            Total: <span style={{ color: "#3b6d11" }}>{fmt(totalCost)}</span>
          </div>
        </div>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}
      >
        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Payment method</div>
          <ToggleGroup
            options={PAYMENT_METHODS}
            value={form.payment_method}
            onChange={(v) => setF("payment_method", v)}
          />
        </div>

        <div
          style={{
            background: "#fff",
            border: "0.5px solid #e8e8e8",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div style={sectionHeaderStyle}>Billing</div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <div>
              <label style={labelStyle}>Bill to</label>
              <ToggleGroup
                options={BILL_TO_OPTIONS}
                value={form.bill_to}
                onChange={(v) => setF("bill_to", v)}
              />
            </div>
            {form.bill_to === "Driver" && (
              <Field label="Select driver">
                <select
                  style={selectStyle}
                  value={form.bill_to_driver_id}
                  onChange={(e) => setF("bill_to_driver_id", e.target.value)}
                >
                  <option value="">Select driver...</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.first_name} {d.last_name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {form.bill_to === "Other" && (
              <Field label="Specify">
                <input
                  style={inputStyle}
                  value={form.bill_to_other}
                  onChange={(e) => setF("bill_to_other", e.target.value)}
                  placeholder="Other party name"
                />
              </Field>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <Field label="Charge amount">
                <input
                  type="number"
                  style={inputStyle}
                  value={form.charge_amount}
                  onChange={(e) => setF("charge_amount", e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                />
              </Field>
              <Field label="Outstanding balance">
                <input
                  style={{
                    ...inputStyle,
                    background: "#f5f5f5",
                    color: "#6b7280",
                  }}
                  value={fmt(outstanding)}
                  readOnly
                />
              </Field>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          border: "0.5px solid #e8e8e8",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={sectionHeaderStyle}>Notes & attachments</div>
        <div style={{ marginBottom: "12px" }}>
          <RichTextEditor
            value={form.notes}
            onChange={(v) => setF("notes", v)}
          />
        </div>

        {/* Saved attachments */}
        {savedAttachments.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                marginBottom: "6px",
              }}
            >
              Saved files
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {savedAttachments.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: LOGO_GREEN_LIGHT,
                    color: LOGO_GREEN,
                    borderRadius: "6px",
                    padding: "5px 10px",
                    fontSize: "12px",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                >
                  {a.file_type?.startsWith("image/") ? "🖼️" : "📄"}{" "}
                  {a.filename || "File"}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* New attachments pending upload */}
        {attachments.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                marginBottom: "6px",
              }}
            >
              Files to upload
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {attachments.map((a, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "#f5f5f5",
                    borderRadius: "6px",
                    padding: "5px 10px",
                    fontSize: "12px",
                    color: "#111",
                  }}
                >
                  {a.preview ? (
                    <img
                      src={a.preview}
                      alt=""
                      style={{
                        width: "32px",
                        height: "24px",
                        objectFit: "cover",
                        borderRadius: "3px",
                      }}
                    />
                  ) : (
                    <span>📄</span>
                  )}
                  <span
                    style={{
                      maxWidth: "120px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.name}
                  </span>
                  <button
                    onClick={() =>
                      setAttachments((p) => p.filter((_, idx) => idx !== i))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#9ca3af",
                      fontSize: "14px",
                      lineHeight: 1,
                      padding: "0 2px",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: "1.5px dashed #e0e0e0",
            borderRadius: "8px",
            padding: "20px",
            textAlign: "center",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = LOGO_GREEN)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
        >
          <label style={{ cursor: "pointer" }}>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/jpg,image/png"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div style={{ fontSize: "22px", marginBottom: "6px" }}>📎</div>
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Drag & drop or click to upload
            </div>
            <div
              style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}
            >
              PDF, JPEG, JPG, PNG
            </div>
          </label>
        </div>
      </div>

      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button
          onClick={onClose}
          style={{
            background: "#f0f0f0",
            color: "#555",
            fontSize: "13px",
            fontWeight: "500",
            padding: "8px 18px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Close
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            background: saving ? "#9ca3af" : LOGO_GREEN,
            color: "#fff",
            fontSize: "13px",
            fontWeight: "500",
            padding: "8px 18px",
            borderRadius: "8px",
            border: "none",
            cursor: saving ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {saving ? "Saving..." : "Save order"}
        </button>
      </div>
    </div>
  );
}

export default function Maintenance() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [tab, setTab] = useState("schedule");
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lastServices, setLastServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ ...DEFAULT_SCHEDULE });
  const [orderForm, setOrderForm] = useState({ ...DEFAULT_ORDER });
  const [lineItems, setLineItems] = useState([{ ...DEFAULT_LINE_ITEM }]);
  const [attachments, setAttachments] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchSchedule, setSearchSchedule] = useState("");
  const [searchOrder, setSearchOrder] = useState("");
  const [filterOrderStatus, setFilterOrderStatus] = useState("");
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  useEffect(() => {
    fetchAll();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [
      trucksRes,
      driversRes,
      stRes,
      schRes,
      ordRes,
      lsRes,
    ] = await Promise.all([
      supabase
        .from("trucks")
        .select("id, unit_number, make, year, license_plate, state")
        .eq("status", "active")
        .order("unit_number"),
      supabase
        .from("drivers")
        .select("id, first_name, last_name")
        .order("first_name"),
      supabase.from("maintenance_service_types").select("*").order("name"),
      supabase
        .from("maintenance_schedules")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("maintenance_orders")
        .select("*, trucks(unit_number, make)")
        .order("created_at", { ascending: false }),
      supabase.from("maintenance_last_service").select("*"),
    ]);
    setTrucks(trucksRes.data || []);
    setDrivers(driversRes.data || []);
    setServiceTypes(stRes.data || []);
    setSchedules(schRes.data || []);
    setOrders(ordRes.data || []);
    setLastServices(lsRes.data || []);
    setLoading(false);
  };

  const setSchF = (key, val) => setScheduleForm((p) => ({ ...p, [key]: val }));
  const setOrdF = (key, val) => setOrderForm((p) => ({ ...p, [key]: val }));

  const resetScheduleForm = () => {
    setScheduleForm({ ...DEFAULT_SCHEDULE });
    setEditingSchedule(null);
  };
  const resetOrderForm = () => {
    setOrderForm({ ...DEFAULT_ORDER });
    setLineItems([{ ...DEFAULT_LINE_ITEM }]);
    setAttachments([]);
    setSavedAttachments([]);
    setEditingOrder(null);
  };

  const openEditSchedule = (s) => {
    setScheduleForm({
      ...DEFAULT_SCHEDULE,
      ...s,
      service_type_ids:
        s.service_type_ids || (s.service_type_id ? [s.service_type_id] : []),
      service_type_names:
        s.service_type_names ||
        (s.service_type_name ? [s.service_type_name] : []),
    });
    setEditingSchedule(s);
    setShowScheduleForm(true);
  };

  const openEditOrder = async (o) => {
    setOrderForm({ ...DEFAULT_ORDER, ...o });
    const [itemsRes, attachRes] = await Promise.all([
      supabase.from("maintenance_order_items").select("*").eq("order_id", o.id),
      supabase.from("maintenance_attachments").select("*").eq("order_id", o.id),
    ]);
    setLineItems(
      itemsRes.data?.length > 0 ? itemsRes.data : [{ ...DEFAULT_LINE_ITEM }]
    );
    setSavedAttachments(attachRes.data || []);
    setAttachments([]);
    setEditingOrder(o);
    setShowOrderForm(true);
  };

  const saveSchedule = async () => {
    if (
      !scheduleForm.service_type_names.length ||
      scheduleForm.truck_ids.length === 0
    ) {
      alert("Please select at least one service type and one truck");
      return;
    }
    setSaving(true);
    const payload = {
      service_type_id: scheduleForm.service_type_ids[0] || null,
      service_type_name: scheduleForm.service_type_names[0] || null,
      service_type_ids: scheduleForm.service_type_ids,
      service_type_names: scheduleForm.service_type_names,
      description: scheduleForm.description || null,
      payment_method: scheduleForm.payment_method || null,
      equipment_type: "truck",
      truck_ids: scheduleForm.truck_ids,
      frequency_unit: scheduleForm.frequency_unit,
      frequency_value: parseInt(scheduleForm.frequency_value) || null,
      lead_days: parseInt(scheduleForm.lead_days) || null,
      status: scheduleForm.status,
    };
    if (editingSchedule) {
      await supabase
        .from("maintenance_schedules")
        .update(payload)
        .eq("id", editingSchedule.id);
    } else {
      await supabase.from("maintenance_schedules").insert(payload);
    }
    await fetchAll();
    setShowScheduleForm(false);
    resetScheduleForm();
    setSaving(false);
  };

  const saveOrder = async () => {
    if (!orderForm.truck_id) {
      alert("Please select a truck");
      return;
    }
    setSaving(true);
    const totalCost = lineItems.reduce(
      (s, i) => s + parseFloat(i.amount || 0),
      0
    );
    const outstanding = totalCost - parseFloat(orderForm.charge_amount || 0);
    const payload = {
      invoice_number: orderForm.invoice_number || null,
      truck_id: orderForm.truck_id,
      odometer: orderForm.odometer || null,
      engine_hours: orderForm.engine_hours || null,
      repair_type: orderForm.repair_type,
      start_date: orderForm.start_date || null,
      due_date: orderForm.due_date || null,
      vendor: orderForm.vendor || null,
      payment_method: orderForm.payment_method || null,
      bill_to: orderForm.bill_to,
      bill_to_driver_id: orderForm.bill_to_driver_id || null,
      bill_to_other: orderForm.bill_to_other || null,
      charge_amount: parseFloat(orderForm.charge_amount || 0),
      total_cost: totalCost,
      outstanding_balance: outstanding,
      notes: orderForm.notes || null,
      status: orderForm.status,
    };
    let orderId = editingOrder?.id;
    if (editingOrder) {
      await supabase
        .from("maintenance_orders")
        .update(payload)
        .eq("id", editingOrder.id);
      await supabase
        .from("maintenance_order_items")
        .delete()
        .eq("order_id", orderId);
    } else {
      const { data } = await supabase
        .from("maintenance_orders")
        .insert(payload)
        .select()
        .single();
      orderId = data?.id;
    }
    if (orderId && lineItems.length > 0) {
      await supabase.from("maintenance_order_items").insert(
        lineItems.map((i) => ({
          order_id: orderId,
          service_type: i.service_type,
          description: i.description || null,
          quantity: parseFloat(i.quantity || 1),
          unit_rate: parseFloat(i.unit_rate || 0),
          amount: parseFloat(i.amount || 0),
        }))
      );
    }
    // Upload attachments
    for (const a of attachments) {
      if (!a.file) continue;
      try {
        const ext = a.name.split(".").pop().toLowerCase();
        const fileName = `maintenance/${orderId}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("driver-docs")
          .upload(fileName, a.file, { contentType: a.type, upsert: true });
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("driver-docs")
            .getPublicUrl(fileName);
          await supabase.from("maintenance_attachments").insert({
            order_id: orderId,
            url: urlData.publicUrl,
            filename: a.name,
            file_type: a.type,
          });
        } else {
          console.error("Upload error:", uploadError);
        }
      } catch (e) {
        console.error("Attachment upload error:", e);
      }
    }
    await fetchAll();
    setShowOrderForm(false);
    resetOrderForm();
    setSaving(false);
  };

  const filteredSchedules = schedules.filter(
    (s) =>
      !searchSchedule ||
      (s.service_type_names || [s.service_type_name])
        .join(", ")
        .toLowerCase()
        .includes(searchSchedule.toLowerCase())
  );

  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      !searchOrder ||
      o.invoice_number?.toLowerCase().includes(searchOrder.toLowerCase()) ||
      o.trucks?.make?.toLowerCase().includes(searchOrder.toLowerCase());
    const matchStatus = !filterOrderStatus || o.status === filterOrderStatus;
    return matchSearch && matchStatus;
  });

  const getTruckNames = (ids) => {
    if (!ids || ids.length === 0) return "—";
    return ids
      .map((id) => {
        const t = trucks.find((t) => t.id === id);
        return t ? `Unit #${t.unit_number}` : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const tabStyle = (active) => ({
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: "500",
    color: active ? LOGO_GREEN : "#6b7280",
    border: "none",
    background: "none",
    cursor: "pointer",
    borderBottom: active ? `2px solid ${LOGO_GREEN}` : "2px solid transparent",
    marginBottom: "-0.5px",
    fontFamily: "inherit",
  });

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8f8f8" }}>
      {!isMobile && <Sidebar />}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile && <MobileHeader title="Maintenance" />}

        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="t-page-title">Repair & Maintenance</div>
          <div style={{ display: "flex", gap: "8px" }}>
            {tab === "schedule" && !showScheduleForm && (
              <button
                onClick={() => {
                  resetScheduleForm();
                  setShowScheduleForm(true);
                }}
                style={{
                  background: LOGO_GREEN,
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                + New schedule
              </button>
            )}
            {tab === "orders" && !showOrderForm && (
              <button
                onClick={() => {
                  resetOrderForm();
                  setShowOrderForm(true);
                }}
                style={{
                  background: LOGO_GREEN,
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                + New order
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderBottom: "0.5px solid #e8e8e8",
            padding: "0 20px",
            display: "flex",
          }}
        >
          <button
            onClick={() => {
              setTab("schedule");
              setShowScheduleForm(false);
              setShowOrderForm(false);
            }}
            style={tabStyle(tab === "schedule")}
          >
            Schedule{" "}
            <span
              style={{
                marginLeft: "6px",
                fontSize: "11px",
                background: LOGO_GREEN_LIGHT,
                color: LOGO_GREEN,
                padding: "1px 6px",
                borderRadius: "10px",
              }}
            >
              {schedules.length}
            </span>
          </button>
          <button
            onClick={() => {
              setTab("orders");
              setShowScheduleForm(false);
              setShowOrderForm(false);
            }}
            style={tabStyle(tab === "orders")}
          >
            Orders{" "}
            <span
              style={{
                marginLeft: "6px",
                fontSize: "11px",
                background: "#e6f1fb",
                color: "#185fa5",
                padding: "1px 6px",
                borderRadius: "10px",
              }}
            >
              {orders.length}
            </span>
          </button>
        </div>

        <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>
          {tab === "schedule" && !showScheduleForm && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  placeholder="Search service type..."
                  value={searchSchedule}
                  onChange={(e) => setSearchSchedule(e.target.value)}
                  style={{ ...inputStyle, width: "240px" }}
                />
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {!isMobile && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr 0.5fr",
                      padding: "10px 16px",
                      background: "#fafafa",
                      borderBottom: "0.5px solid #e8e8e8",
                    }}
                  >
                    {[
                      "Service type(s)",
                      "Equipment",
                      "Frequency",
                      "Lead time",
                      "Status",
                      "",
                    ].map((h) => (
                      <div key={h} className="t-table-header">
                        {h}
                      </div>
                    ))}
                  </div>
                )}
                {loading ? (
                  <div
                    style={{ padding: "40px", textAlign: "center" }}
                    className="t-body"
                  >
                    Loading...
                  </div>
                ) : filteredSchedules.length === 0 ? (
                  <div
                    style={{ padding: "40px", textAlign: "center" }}
                    className="t-body"
                  >
                    No schedules yet — click New schedule to get started
                  </div>
                ) : (
                  filteredSchedules.map((s, i) => (
                    <div
                      key={s.id}
                      onClick={() => openEditSchedule(s)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "2fr 1.5fr 1fr 1fr 1fr 0.5fr",
                        padding: "12px 16px",
                        borderBottom: "0.5px solid #f0f0f0",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "background 0.15s",
                        background: i % 2 === 1 ? "#fafafa" : "#fff",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f5f5f5")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          i % 2 === 1 ? "#fafafa" : "#fff")
                      }
                    >
                      <div className="t-name">
                        {(s.service_type_names || [s.service_type_name]).join(
                          ", "
                        )}
                      </div>
                      <div className="t-label" style={{ color: "#6b7280" }}>
                        {getTruckNames(s.truck_ids)}
                      </div>
                      <div className="t-data">
                        Every {s.frequency_value} {s.frequency_unit}
                      </div>
                      <div className="t-data">
                        {s.lead_days ? `${s.lead_days} days` : "—"}
                      </div>
                      <div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            ...getStatusStyle(s.status),
                          }}
                        >
                          {s.status}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: LOGO_GREEN,
                          fontWeight: "500",
                        }}
                      >
                        Edit →
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "schedule" && showScheduleForm && (
            <ScheduleForm
              form={scheduleForm}
              setF={setSchF}
              trucks={trucks}
              serviceTypes={serviceTypes}
              lastServices={lastServices}
              onSave={saveSchedule}
              onClose={() => {
                setShowScheduleForm(false);
                resetScheduleForm();
              }}
              saving={saving}
            />
          )}

          {tab === "orders" && !showOrderForm && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <input
                  placeholder="Search invoice, truck..."
                  value={searchOrder}
                  onChange={(e) => setSearchOrder(e.target.value)}
                  style={{ ...inputStyle, width: "240px" }}
                />
                <select
                  value={filterOrderStatus}
                  onChange={(e) => setFilterOrderStatus(e.target.value)}
                  style={{ ...selectStyle, width: "auto", padding: "7px 12px" }}
                >
                  <option value="">All statuses</option>
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "0.5px solid #e8e8e8",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {!isMobile && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "1fr 1.2fr 1fr 0.8fr 0.8fr 1fr 0.5fr",
                      padding: "10px 16px",
                      background: "#fafafa",
                      borderBottom: "0.5px solid #e8e8e8",
                    }}
                  >
                    {[
                      "Invoice #",
                      "Truck",
                      "Vendor",
                      "Total",
                      "Bill to",
                      "Status",
                      "",
                    ].map((h) => (
                      <div key={h} className="t-table-header">
                        {h}
                      </div>
                    ))}
                  </div>
                )}
                {loading ? (
                  <div
                    style={{ padding: "40px", textAlign: "center" }}
                    className="t-body"
                  >
                    Loading...
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div
                    style={{ padding: "40px", textAlign: "center" }}
                    className="t-body"
                  >
                    No orders yet — click New order to get started
                  </div>
                ) : (
                  filteredOrders.map((o, i) => (
                    <div
                      key={o.id}
                      onClick={() => openEditOrder(o)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "1fr 1.2fr 1fr 0.8fr 0.8fr 1fr 0.5fr",
                        padding: "12px 16px",
                        borderBottom: "0.5px solid #f0f0f0",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "background 0.15s",
                        background: i % 2 === 1 ? "#fafafa" : "#fff",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f5f5f5")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          i % 2 === 1 ? "#fafafa" : "#fff")
                      }
                    >
                      <div className="t-name">{o.invoice_number || "—"}</div>
                      <div className="t-data">
                        {o.trucks
                          ? `Unit #${o.trucks.unit_number} — ${o.trucks.make}`
                          : "—"}
                      </div>
                      <div className="t-data">{o.vendor || "—"}</div>
                      <div
                        className="t-data"
                        style={{ color: "#3b6d11", fontWeight: "500" }}
                      >
                        {fmt(o.total_cost)}
                      </div>
                      <div className="t-data">{o.bill_to}</div>
                      <div>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "2px 8px",
                            borderRadius: "20px",
                            ...getStatusStyle(o.status),
                          }}
                        >
                          {o.status.replace("_", " ")}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: LOGO_GREEN,
                          fontWeight: "500",
                        }}
                      >
                        View →
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "orders" && showOrderForm && (
            <OrderForm
              form={orderForm}
              setF={setOrdF}
              lineItems={lineItems}
              setLineItems={setLineItems}
              trucks={trucks}
              drivers={drivers}
              attachments={attachments}
              setAttachments={setAttachments}
              savedAttachments={savedAttachments}
              onSave={saveOrder}
              onClose={() => {
                setShowOrderForm(false);
                resetOrderForm();
              }}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
}
